import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { http, apiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauth, object = auth
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await http.get("/auth/me");
      setUser(data);
    } catch (e) {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await http.post("/auth/login", { email, password });
      setUser(data);
      return data;
    } catch (e) {
      const msg = apiError(e, "Login failed");
      setError(msg);
      throw new Error(msg);
    }
  };

  const register = async (email, password, name, ref_code) => {
    setError("");
    try {
      const { data } = await http.post("/auth/register", { email, password, name, ref_code });
      setUser(data);
      return data;
    } catch (e) {
      const msg = apiError(e, "Registration failed");
      setError(msg);
      throw new Error(msg);
    }
  };

  const logout = async () => {
    try {
      await http.post("/auth/logout");
    } catch (e) { /* ignore */ }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, error, setError, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
